import React, { useEffect, useState } from 'react';
import { Grid, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { fetchScheduleMetadata, fetchGPCILocalityData, GPCILocalityData } from '../../../store/api/globalFeeSchedule';
import { GlobalFeeScheduleMetadata } from '../../../types/globalFeeScheduleTypes';
import StateDropdown from './components/StateDropdown';
import LocalityDropdown from './components/LocalityDropdown';
import GPCIDropdown from './components/GPCIDropdown';

interface GlobalFeeScheduleFieldsProps {
    scheduleId: number;
    isRBRVS?: boolean; // Optional: if provided, skip API call
    values: {
        selectedRateType: string | null;
        selectedState: string | null;
        selectedLocality: string | null;
        selectedGpci: string | null;
        isDME?:Boolean
    };
    onChange: (field: string, value: any) => void;
    errors: Record<string, string>;
}

const GlobalFeeScheduleFields: React.FC<GlobalFeeScheduleFieldsProps> = ({
    scheduleId,
    isRBRVS: isRBRVSProp,
    values,
    onChange,
    errors,
    
}) => {
    const [metadata, setMetadata] = useState<GlobalFeeScheduleMetadata | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [gpciLocalityData, setGpciLocalityData] = useState<GPCILocalityData[]>([]);

    const isDME=values?.isDME

    // Fetch GPCI locality data once (with caching from store/api)
    useEffect(() => {
        fetchGPCILocalityData()
            .then(data => setGpciLocalityData(data))
            .catch(err => setError('Failed to load regional data'));
    }, []);

    useEffect(() => {
        // If isRBRVS is provided as prop, create metadata directly without API call
        if (isRBRVSProp !== undefined) {
            if (isRBRVSProp) {
                // Create metadata for RBRVS schedule
                setMetadata({
                    id: scheduleId,
                    name: `Fee Schedule ${scheduleId}`,
                    description: `${isDME ? "DME":"RBRVS"} Fee Schedule`,
                    isRBRVS: true,
                    requires_state: true,
                    requires_locality: isDME ? false:true,
                    requires_gpci: isDME ? false:true,
                    rate_types: [
                        { name: 'Non-Facility', column: 'rate', description: 'Non-Facility Rate' },
                        { name: 'Facility', column: 'rate_1', description: 'Facility Rate' }
                    ]
                });
                setLoading(false);
            } else {
                // CLAB schedule - no metadata needed
                setMetadata(null);
                setLoading(false);
            }
            return;
        }
        
        // Otherwise, fetch metadata from API (but we're using isRBRVS prop, so this shouldn't run)
        if (scheduleId && !metadata) {
            setLoading(true);
            setError(null);
            
            fetchScheduleMetadata(scheduleId)
                .then(data => {
                    setMetadata(data);
                    setLoading(false);
                })
                .catch(err => {
                    setError('Failed to load schedule metadata');
                    setLoading(false);
                });
        }
    }, [scheduleId, isRBRVSProp]);


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" ml={2}>Loading schedule details...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">{error}</Alert>
        );
    }

    if (!metadata) {
        return null;
    }


    // Only show regional fields for RBRVS schedules
    // isRBRVS = true means RBRVS schedule (show regional fields)
    // isRBRVS = false means CLAB schedule (don't show regional fields)
    if (!metadata.isRBRVS) {
        return null; // Don't show any fields for CLAB schedules
    }

    // For RBRVS schedules, show State, GPCI, and Locality fields
    const showRegionalFields = metadata.isRBRVS && (metadata.requires_state || metadata.requires_locality || metadata.requires_gpci);

    if (!showRegionalFields) {
        return null;
    }


    return (
        <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Regional Configuration {isDME ? '(DME)':'(RBRVS)'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {isDME ? 'Select State' : 'Select State, GPCI, and Locality for this RBRVS fee schedule'}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* Field Order: State → GPCI → Locality */}
                
                {/* 1. State Dropdown */}
                {metadata.requires_state && (
                    <Grid item xs={12} md={4}>
                        <StateDropdown
                            scheduleId={scheduleId}
                            value={values.selectedState}
                            onChange={(value) => {
                                onChange('selectedState', value);
                                // Reset GPCI and locality when state changes
                                if (value !== values.selectedState) {
                                    onChange('selectedGpci', null);
                                    onChange('selectedLocality', null);
                                }
                            }}
                            onBlur={() => {
                                onChange('selectedState', values.selectedState || '');
                            }}
                            error={errors.selectedState}
                            required={true}
                        />
                    </Grid>
                )}

                {/* 2. GPCI Dropdown */}
                {metadata.requires_gpci && (
                    <Grid item xs={12} md={4}>
                        <GPCIDropdown
                            selectedState={values.selectedState}
                            gpciLocalityData={gpciLocalityData}
                            value={values.selectedGpci}
                            onChange={(value) => {
                                onChange('selectedGpci', value);
                                // Reset locality when GPCI changes
                                if (value !== values.selectedGpci) {
                                    onChange('selectedLocality', null);
                                }
                            }}
                            onBlur={() => {
                                onChange('selectedGpci', values.selectedGpci || '');
                            }}
                            error={errors.selectedGpci}
                            required={true}
                        />
                    </Grid>
                )}

                {/* 3. Locality Dropdown */}
                {metadata.requires_locality && (
                    <Grid item xs={12} md={4}>
                        <LocalityDropdown
                            selectedState={values.selectedState}
                            selectedGpci={values.selectedGpci}
                            gpciLocalityData={gpciLocalityData}
                            value={values.selectedLocality}
                            onChange={(value) => onChange('selectedLocality', value)}
                            onBlur={() => {
                                onChange('selectedLocality', values.selectedLocality || '');
                            }}
                            error={errors.selectedLocality}
                            required={true}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default GlobalFeeScheduleFields;
